import { alpha, Button, Stack } from "@mui/material";

export const WEARABLE_RANGE_PRESETS = [1, 5, 10, 20, 30] as const;
export type WearableRangeDays = (typeof WEARABLE_RANGE_PRESETS)[number];

/** Presets do dashboard fitness do paciente (sem 10 dias). */
export const FITNESS_RANGE_PRESETS = [1, 5, 20, 30] as const;
export type FitnessRangeDays = (typeof FITNESS_RANGE_PRESETS)[number];

/** Presets da página FluxHealth (Wearable): 1, 7, 15 e 30 dias. */
export const FLUXHEALTH_RANGE_PRESETS = [1, 7, 15, 30] as const;
export type FluxHealthRangeDays = (typeof FLUXHEALTH_RANGE_PRESETS)[number];

export function isValidFitnessRangeDays(value: number): value is FitnessRangeDays {
  return (FITNESS_RANGE_PRESETS as readonly number[]).includes(value);
}

export function isValidFluxHealthRangeDays(
  value: number
): value is FluxHealthRangeDays {
  return (FLUXHEALTH_RANGE_PRESETS as readonly number[]).includes(value);
}

/** Rótulos em quantidade de dias dos intervalos da página FluxHealth. */
export function formatFluxHealthRangeLabel(days: number): string {
  return days === 1 ? "1 dia" : `${days} dias`;
}

export function isValidWearableRangeDays(value: number): value is WearableRangeDays {
  return (WEARABLE_RANGE_PRESETS as readonly number[]).includes(value);
}

/** Fallback para valores legados (ex.: preset 15 removido). */
export function normalizeWearableRangeDays(value: number): WearableRangeDays {
  if (isValidWearableRangeDays(value)) return value;
  if (value === 15) return 20;
  return 10;
}

interface WearableDateRangeChipsProps {
  rangeDays: number;
  /** Aceita number para permitir presets FluxHealth (7, 15) além dos do atendimento. */
  onChange: (days: number) => void;
  disabled?: boolean;
  tone?: "default" | "onPrimary";
  /** Lista de presets; padrão = WEARABLE_RANGE_PRESETS (atendimento). */
  presets?: readonly number[];
  /** Texto do chip; padrão = "1 dia" / "N dias". */
  formatLabel?: (days: number) => string;
}

export function WearableDateRangeChips({
  rangeDays,
  onChange,
  disabled = false,
  tone = "default",
  presets = WEARABLE_RANGE_PRESETS,
  formatLabel,
}: WearableDateRangeChipsProps) {
  const isOnPrimary = tone === "onPrimary";

  return (
    <Stack
      direction="row"
      spacing={0.5}
      flexWrap="nowrap"
      useFlexGap
      justifyContent="center"
      sx={{
        width: "100%",
        minWidth: 0,
      }}
    >
      {presets.map((preset) => {
        const isActive = rangeDays === preset;

        return (
          <Button
            key={preset}
            size="small"
            variant={isOnPrimary ? "text" : isActive ? "contained" : "outlined"}
            color={isOnPrimary ? "inherit" : "primary"}
            disabled={disabled}
            onClick={() => onChange(preset)}
            sx={{
              minWidth: "auto",
              px: { xs: 0.85, sm: 1.25 },
              flexShrink: 0,
              fontWeight: isActive ? 700 : 500,
              fontSize: { xs: "0.72rem", sm: "0.8125rem" },
              whiteSpace: "nowrap",
              ...(isOnPrimary && {
                color: "white",
                border: "1px solid",
                borderColor: isActive
                  ? (t) => alpha(t.palette.common.white, 0.85)
                  : (t) => alpha(t.palette.common.white, 0.35),
                bgcolor: isActive
                  ? (t) => alpha(t.palette.common.white, 0.22)
                  : "transparent",
                "&:hover": {
                  bgcolor: isActive
                    ? (t) => alpha(t.palette.common.white, 0.28)
                    : (t) => alpha(t.palette.common.white, 0.1),
                  borderColor: (t) => alpha(t.palette.common.white, 0.55),
                },
                "&.Mui-disabled": {
                  color: (t) => alpha(t.palette.common.white, 0.35),
                  borderColor: (t) => alpha(t.palette.common.white, 0.15),
                },
              }),
            }}
          >
            {formatLabel
              ? formatLabel(preset)
              : preset === 1
                ? "1 dia"
                : `${preset} dias`}
          </Button>
        );
      })}
    </Stack>
  );
}
